import React, { useEffect, useState, Suspense, useContext, useRef, startTransition } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { useLoader } from './contexts/LoaderContext.js';
import Loader from './components/common/Loader.js';
import { useTheme } from './contexts/ThemeContext.js';
import { UserContext } from './contexts/UserContext.js';
import Favourites from './components/Favourites/index.js';
import ErrorPage from './components/ErrorPage/index.js';
import { useMessage } from './contexts/MessageContext';
import { Button } from 'antd';
import { UpCircleOutlined } from '@ant-design/icons';

import config from './config.json';
import { useRefresh } from './contexts/RefreshContext.js';
import RegisterUser from './components/common/RegisterUser.js';
import TestPage from './components/TestPage/index.js';
import { syncTawk, destroyTawk } from './utils/tawkService.js';
// Loaded up front: registers window.kwRefreshWorkspace for the offline refresh.
import './utils/Data';

// Platform-aware screens — the registry selects web or mobile automatically.
import {
  ApplicationSelector,
  DeleteAccount,
  LoadingScreen,
  Login,
  Help,
  Settings,
  CommunitySettings,
  UserSettings,
  WorkspaceSettings,
  PageWithBottomBar,
  NotificationsPage,
} from './components/platform';

const Logout = React.lazy(() => import('./components/common/Logout.js'));
const PasswordChange  = React.lazy(() => import('./components/common/PasswordChange.js'));
const Context         = React.lazy(() => import('./views/Context.js'));
const PortalApp       = React.lazy(() => import('./portal/PortalApp.js'));
const PortalPage      = React.lazy(() => import('./portal/PortalPage.js'));
const PortalPageEditor= React.lazy(() => import('./portal/editor/PortalPageEditor.js'));
const HubDashboardsPage  = React.lazy(() => import('./portal/HubDashboardsPage.js'));
const DashboardLanding   = React.lazy(() => import('./portal/DashboardLanding.js'));


// Shared props for every authenticated PageWithBottomBar route.
// Not-yet-authenticated visits to these are never worth returning to.
const NON_RESUMABLE_PATHS = ['/logout', '/error', '/dashboard'];

function AuthRoute({ isLoggedIn, sidebarRef, hubSettings, activeMQ, triggerRefresh, onTabChange, children }) {
  const location = useLocation();
  if (!isLoggedIn) {
    // Deep link opened while logged out (e.g. a "raise a ticket" link in an
    // email) — remember the full path + query so login lands back on it
    // (consumed by Login's handleLogin / the post-auth effect below) instead
    // of the default /dashboard. Logout always reloads the page, so this
    // never captures the page a user was on when they signed out.
    const target = location.pathname + location.search;
    if (!window._pendingDeepLink && !NON_RESUMABLE_PATHS.includes(location.pathname)) {
      window._pendingDeepLink = target;
    }
    return <Navigate to="/login" replace />;
  }
  return (
    <PageWithBottomBar
      sidebarRef={sidebarRef}
      hubSettings={hubSettings}
      activeMQ={activeMQ}
      triggerRefresh={triggerRefresh}
      onTabChange={onTabChange}
    >
      {children}
    </PageWithBottomBar>
  );
}

export default function App() {
  const navigate = useNavigate();
  const showMessage = useMessage();
  const isMobile = Boolean(window.Capacitor?.isNativePlatform() || window.cordova || /Mobi|Android/i.test(navigator.userAgent));
  // Narrower than isMobile: true only for the native iOS/Android shell, not a
  // mobile browser. A single native build serves many hubs (no fixed domain),
  // so it must resolve its hub via Login's preAuthHubMappings step-flow. A
  // mobile browser hitting a hub's own domain can be matched by URL exactly
  // like desktop web.
  const isNativeApp = Boolean(window.Capacitor?.isNativePlatform() || window.cordova);

  const { setUser } = useContext(UserContext);
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const { startLoader, stopLoader, loading } = useLoader();
  const { setHubCode, setActiveHub } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [activeMQ, setActiveMQ] = useState(null);
  const { triggerRefresh } = useRefresh();
  const sidebarRef = useRef(null);
  const [hubSettings, setHubSettings] = useState();

  // Scroll-to-top visibility
  const toggleVisibility = () => setIsVisible(window.scrollY > window.innerHeight);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Keep a ref to navigate so deep link effects can call it without navigate as a dependency.
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; });

  // Stay awake: keep the screen from dimming/locking while the native app is
  // open (same as KwantuGo/BetterData). Re-applied whenever the app returns to
  // the foreground in case the OS reset it while in the background.
  useEffect(() => {
    if (!isNativeApp) return undefined;
    const stayAwake = () => KeepAwake.keepAwake().catch((err) => console.log('KeepAwake failed', err));
    stayAwake();
    let stateHandle;
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) stayAwake();
    }).then((handle) => { stateHandle = handle; });
    return () => { stateHandle?.remove(); };
  }, [isNativeApp]);

  useEffect(() => {
    if (!window.Capacitor) return;
    let listenerHandle;
    CapacitorApp.addListener('appUrlOpen', (event) => {
      try {
        const parsed = new URL(event.url);
        const path = parsed.pathname + parsed.search;
        if (!path || path === '/') return;
        if (window.app?.authenticated) {
          navigateRef.current(path, { replace: true });
        } else {
          window._pendingDeepLink = path;
        }
      } catch {}
    }).then((handle) => { listenerHandle = handle; });
    return () => { listenerHandle?.remove(); };
  }, []); 

  useEffect(() => {
    if (!loadingComplete || !isLoggedIn) return;
    const path = window._pendingDeepLink;
    if (path === undefined) return;
    window._pendingDeepLink = undefined;
    if (path) navigateRef.current(path, { replace: true });
  }, [loadingComplete, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Global event listeners
  useEffect(() => {
    const events = [
      ['successEvent',    (e) => showMessage(e.detail.text, 'success')],
      ['errorEvent',      (e) => showMessage(e.detail.text, 'error')],
      ['infoEvent',       (e) => showMessage(e.detail.text, 'info')],
      ['warningEvent',    (e) => showMessage(e.detail.text, 'warning')],
      ['logEvent',        () => {}],
      // MQ status isn't urgent enough to force a synchronous re-render while a
      // lazy route is still suspended -- startTransition lets React finish it.
      ['mqEvent',         (e) => startTransition(() => setActiveMQ(e.detail.text))],
      ['startLoaderEvent', startLoader],
      ['stopLoaderEvent',  stopLoader],
      ['scroll',          toggleVisibility],
    ];
    events.forEach(([event, handler]) => document.addEventListener(event, handler));
    return () => events.forEach(([event, handler]) => document.removeEventListener(event, handler));
  }, [showMessage, startLoader, stopLoader]);

  // Redirect unauthenticated users (skip web public-hub portal paths)
  useEffect(() => {
    if (loadingComplete) {
      const publicPortalEnabled = !isMobile && !!hubSettings?.domainId && hubSettings?.public !== false;
      const publicPaths = ['/login', '/register', '/password', '/test'];
      const path = window.location.pathname;
      const isPublicPath = publicPaths.includes(path);
      const isPublicPortalPath = publicPortalEnabled && (path === '/' || path.startsWith('/portal') || path.startsWith('/hub/'));
      if (!window.app.authenticated && !isPublicPath && !isPublicPortalPath) {
        navigate('/login', { replace: true });
      }
    }
  }, [loadingComplete, hubSettings, isMobile, navigate]);

  // Bootstrap: session check + hub config
  useEffect(() => {
    // The native app resolves its hub (and tawk widget) through the Login
    // step-flow instead of a URL match — see components/Login/mobile/Login.js.
    // Mobile browsers, like desktop web, sit on a hub's own domain and can be
    // matched by URL directly.
    const currentURL = window.location.origin;
    const matchedHub = isNativeApp
      ? null
      : config.hubSettings?.find((hub) => hub.url === currentURL);

    if (matchedHub) {
      setHubSettings(matchedHub);
      setHubCode(matchedHub.hubCode);  // activate hub-specific theme
      setActiveHub(matchedHub);        // exact matched entry — authoritative for hub-specific flags like showUserInWorkflow
      // Per-hub favicon: config.json hubFavicons[] -> app/images/favicons/<file> (copied to /external/app/images by copy-images)
      const favicon = config.hubFavicons?.find((f) => f.hubCode === matchedHub.hubCode)?.file;
      if (favicon) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        link.href = `${process.env.PUBLIC_URL || ''}/external/app/images/favicons/${favicon}`;
      }
      if (matchedHub.initialPath !== '/') {
        navigate(matchedHub.initialPath, { replace: true });
      }
      // Web: chatEnabled/chatUUID come solely from the URL-matched config.json
      // entry — no window.CHAT_UUID fallback here (that's mobile-only, driven
      // from Login.js before/after preAuthHubMappings resolves the hub).
      // Mobile browsers keep the mobile-only iframe chat path (see
      // tawkService.js) rather than injecting Tawk's script.
      if (!isMobile) syncTawk(matchedHub);
    } else {
      setHubSettings(undefined);
      setHubCode(null);
      setActiveHub(null);
      if (!isMobile) destroyTawk();
      // Mobile: don't touch tawk here. Login.js already owns the pre-auth
      // default widget (visible, no bottom bar exists yet to hide it behind).
      // Restoring a hub-specific hidden-launcher widget eagerly — before we
      // even know window.auth.session() below will succeed — used to race
      // Login's default load and could hide the widget with no way to reopen
      // it. Only restore once authentication is actually confirmed, below.
    }

    const authenticate = async () => {
      if (window.app.authenticated) {
        setLoadingComplete(true);
        return;
      }
      try {
        const userObject = await window.auth.session();
        window.app.authenticated = true;
        window.app.authorised = true;
        setUser({ firstName: userObject.firstName, lastName: userObject.lastName });
        window.auth.connectMQ();
        if(!window._pendingDeepLink) {
          // Store default navigation target; post-auth effect will navigate after routes render.
          window._pendingDeepLink = (['/', '/login'].includes(window.location.pathname))
            ? '/dashboard' : window.location.pathname + window.location.search;
        }
        setLoggedIn(true);
      } catch {
        window.app.authenticated = false;
      } finally {
        setLoadingComplete(true);
      }
    };
    authenticate();
  }, [isMobile, isNativeApp, navigate, setHubCode, setActiveHub, setUser]);

  function handleTabChange(page) {
    navigate(`/${page === 'home' ? 'dashboard' : page}`);
  }

  if (!loadingComplete) {
    return <LoadingScreen hubSettings={hubSettings} />;
  }

  const authProps = { isLoggedIn, sidebarRef, hubSettings, activeMQ, triggerRefresh, onTabChange: handleTabChange };
  const publicPortalEnabled = !isMobile && !!hubSettings?.domainId;

  return (
    <div className="contentBody">
      {loading && <Loader />}
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login"    element={<Login assignLoggedIn={setLoggedIn} hubSettings={hubSettings} />} />
          <Route path="/register" element={<RegisterUser hubSettings={hubSettings} />} />
          <Route path="/password" element={<PasswordChange hubSettings={hubSettings} />} />
          <Route path="/test"     element={<TestPage />} />

          {/* ── Portal routes (public hub) ─────────────────────────────── */}
          {publicPortalEnabled ? (
            <>
              {/* Root path: logged-in users go straight to /dashboard so the
                  rail (community picker + apps) drives selection. Unauthenticated
                  visitors see the public PortalApp landing page. */}
              <Route
                path="/"
                element={
                  isLoggedIn
                    ? <Navigate to="/dashboard" replace />
                    : hubSettings?.public === false
                      ? <Navigate to="/login" replace />
                      : <PortalApp hubSettings={hubSettings} isLoggedIn={isLoggedIn} triggerRefresh={triggerRefresh} activeMQ={activeMQ} />
                }
              />
              <Route
                path="/hub/:hubCode/:pageId"
                element={
                  !isLoggedIn && hubSettings?.public === false
                    ? <Navigate to="/login" replace />
                    : <PortalPage isLoggedIn={isLoggedIn} domainId={hubSettings?.domainId} />
                }
              />
              <Route
                path="/hub/:hubCode/:pageId/c/:communityId/a/:applicationId"
                element={
                  <AuthRoute {...authProps}>
                    <PortalPage isLoggedIn={isLoggedIn} domainId={hubSettings?.domainId} hideTopBar />
                  </AuthRoute>
                }
              />
              {/* Admin editor (authenticated) */}
              <Route
                path="/portal/admin"
                element={
                  isLoggedIn
                    ? <PortalPageEditor hubSettings={hubSettings} />
                    : <Navigate to="/login" replace />
                }
              />
            </>
          ) : (
            /* Private hub: / redirects based on auth state */
            <Route path="/" element={<Navigate to={isLoggedIn ? "/dashboard" : "/login"} replace />} />
          )}

          {/* Authenticated routes */}
          <Route path="/logout"   element={<AuthRoute {...authProps}><Logout /></AuthRoute>} />
          <Route path="/error"    element={<AuthRoute {...authProps}><ErrorPage /></AuthRoute>} />
          {/* /dashboard landing: redirect to hub dashboards when portal mode is
              active; otherwise show the standard application selector grid.      */}
          <Route
            path="/dashboard"
            element={
              <AuthRoute {...authProps}>
                {publicPortalEnabled && hubSettings?.hubCode
                  ? <DashboardLanding hubCode={hubSettings.hubCode} />
                  : <ApplicationSelector />
                }
              </AuthRoute>
            }
          />
          <Route path="/notifications" element={<AuthRoute {...authProps}><NotificationsPage /></AuthRoute>} />
          <Route path="/favourites" element={<AuthRoute {...authProps}><Favourites /></AuthRoute>} />
          <Route path="/help"     element={<AuthRoute {...authProps}><Help /></AuthRoute>} />
          <Route path="/deleteAccount" element={<AuthRoute {...authProps}><DeleteAccount /></AuthRoute>} />
          <Route path="/settings" element={<AuthRoute {...authProps}><Settings /></AuthRoute>} />
          <Route path="/settings/community" element={<AuthRoute {...authProps}><CommunitySettings /></AuthRoute>} />
          <Route path="/settings/user" element={<AuthRoute {...authProps}><UserSettings /></AuthRoute>} />
          <Route path="/settings/workspace" element={<AuthRoute {...authProps}><WorkspaceSettings /></AuthRoute>} />
          <Route path="/profile"  element={<AuthRoute {...authProps}><Settings /></AuthRoute>} />

          {/* Context / deep-link routes */}
          <Route
            path="/context/:contextId/c/:communityId/a/:applicationId"
            element={<AuthRoute {...authProps}><Context sidebarRef={sidebarRef} back={false} activeMQ={activeMQ} /></AuthRoute>}
          />
          <Route
            path="/context/:contextId/page/:pageId/c/:communityId/a/:applicationId"
            element={<AuthRoute {...authProps}><Context sidebarRef={sidebarRef} activeMQ={activeMQ} /></AuthRoute>}
          />
          <Route
            path="/context/:contextId/page/:pageId/c/:communityId/a/:applicationId/profile/:profileId"
            element={<AuthRoute {...authProps}><Context sidebarRef={sidebarRef} activeMQ={activeMQ} /></AuthRoute>}
          />
          <Route
            path="/context/:contextId/page/:pageId/c/:communityId/a/:applicationId/profile/:profileId/node/:subprofileId"
            element={<AuthRoute {...authProps}><Context sidebarRef={sidebarRef} activeMQ={activeMQ} /></AuthRoute>}
          />
          <Route
            path="/context/:contextId/page/:pageId/c/:communityId/a/:applicationId/profile/:profileId/process/:processId"
            element={<AuthRoute {...authProps}><Context sidebarRef={sidebarRef} activeMQ={activeMQ} /></AuthRoute>}
          />
          <Route
            path="/context/:contextId/page/:pageId/c/:communityId/a/:applicationId/profile/:profileId/node/:subprofileId/process/:processId"
            element={<AuthRoute {...authProps}><Context sidebarRef={sidebarRef} activeMQ={activeMQ} /></AuthRoute>}
          />

          {/* ── Hub Dashboards (authenticated) ──────────────────────────────
              Single consolidated tabbed page replacing the prior per-link rail
              entries. The literal `dashboards` / `my-dashboards` segments out-
              rank the generic `/hub/:hubCode/:pageId` portal route so these
              match first. */}
          <Route
            path="/hub/:hubCode/dashboards"
            element={
              <AuthRoute {...authProps}>
                <HubDashboardsPage scope="hub" domainId={hubSettings?.domainId} isLoggedIn={isLoggedIn} />
              </AuthRoute>
            }
          />
          <Route
            path="/hub/:hubCode/dashboards/c/:communityId/a/:applicationId"
            element={
              <AuthRoute {...authProps}>
                <HubDashboardsPage scope="hub" domainId={hubSettings?.domainId} isLoggedIn={isLoggedIn} />
              </AuthRoute>
            }
          />
          {/* Community-only my-dashboards: community selected but no app yet */}
          <Route
            path="/hub/:hubCode/my-dashboards/c/:communityId"
            element={
              <AuthRoute {...authProps}>
                <HubDashboardsPage scope="community" domainId={hubSettings?.domainId} isLoggedIn={isLoggedIn} />
              </AuthRoute>
            }
          />
          <Route
            path="/hub/:hubCode/my-dashboards/c/:communityId/a/:applicationId"
            element={
              <AuthRoute {...authProps}>
                <HubDashboardsPage scope="community" domainId={hubSettings?.domainId} isLoggedIn={isLoggedIn} />
              </AuthRoute>
            }
          />
        </Routes>
      </Suspense>

      {/* Scroll-to-top button */}
      {isVisible && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999 }}>
          <Button
            type="primary"
            shape="circle"
            icon={<UpCircleOutlined />}
            size="large"
            onClick={scrollToTop}
            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
          />
        </div>
      )}
    </div>
  );
}
